import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { Plus, Phone, Eye, Mail, MapPin, X, TrendingUp, Clock, Calendar, History, FileText, Receipt, Building2, Loader2, Trash2, Save } from 'lucide-react';
import SearchBar, { matches } from '../components/app/SearchBar';
import SitePicker from '../components/app/SitePicker';

const PriorityBadge = ({ p }) => {
  const map = { L1: 'bg-red-500/15 text-red-400 border-red-500/30', L2: 'bg-orange-500/15 text-orange-400 border-orange-500/30', L3: 'bg-white/5 text-white/60 border-white/10' };
  return <span className={`text-xs font-black px-2 py-1 rounded-full border ${map[p] || map.L3}`}>{p}</span>;
};

const StatusBadge = ({ s }) => {
  const map = {
    abierta: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    pendiente: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
    firmada: 'bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/30',
    perdida: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  return <span className={`text-xs font-bold px-2 py-1 rounded-full border ${map[s] || ''} uppercase`}>{s}</span>;
};

const Modal = ({ open, onClose, children, title }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
          <h3 className="text-xl font-black text-white">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const empty = {
  site_id: '', site_code_manual: '', current_rent: 0, proposed_rent: 0, max_offer_pct: 15,
  expiration_date: '', next_action_date: '', next_action_type: 'llamada',
  priority: 'L3', status: 'abierta', notes: '',
};

const Negociaciones = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterP, setFilterP] = useState('');
  const [filterS, setFilterS] = useState('');
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();

  // Deep-link from global search: /app/negociaciones?q=xxx&focus=<id>
  useEffect(() => {
    const q = searchParams.get('q');
    const focus = searchParams.get('focus');
    if (q) setSearch(q);
    if (focus && items.length) {
      const it = items.find((x) => x.id === focus);
      if (it) setDetail(it);
    }
  }, [searchParams, items]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [event, setEvent] = useState({ type: 'llamada', summary: '' });
  const [taxParams, setTaxParams] = useState({ owner_type: 'particular', region: 'peninsula', num_owners: 1, other_income: 0, is_new_company: false });
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);

  useEffect(() => {
    if (!detail) { setTaxData(null); return; }
    const annual = (parseFloat(detail.proposed_rent || detail.current_rent) || 0) * 12;
    if (annual <= 0) { setTaxData(null); return; }
    const t = setTimeout(async () => {
      setTaxLoading(true);
      try {
        const r = await api.post('/fiscal/tax-estimate', {
          annual_rent: annual,
          owner_type: taxParams.owner_type,
          region: taxParams.region,
          num_owners: taxParams.owner_type === 'ccpp' ? (parseInt(taxParams.num_owners) || 1) : 1,
          other_income: parseFloat(taxParams.other_income) || 0,
          is_new_company: !!taxParams.is_new_company,
        });
        setTaxData(r.data);
      } catch (e) { console.error(e); }
      finally { setTaxLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [detail?.id, detail?.current_rent, detail?.proposed_rent, taxParams]);

  const load = async () => {
    const params = {};
    if (filterP) params.priority = filterP;
    if (filterS) params.status = filterS;
    const r = await api.get('/negotiations', { params });
    setItems(r.data);
    const s = await api.get('/sites/lite');
    setSites(s.data);
  };
  useEffect(() => { load(); }, [filterP, filterS]);

  const create = async () => {
    const payload = { ...form,
      // si viene código manual, lo enviamos como site_id (backend acepta cualquier string)
      site_id: form.site_id || form.site_code_manual || '',
      current_rent: parseFloat(form.current_rent) || 0,
      proposed_rent: form.proposed_rent ? parseFloat(form.proposed_rent) : null,
      max_offer_pct: parseFloat(form.max_offer_pct) || 15,
    };
    delete payload.site_code_manual;
    await api.post('/negotiations', payload);
    setModal(false); setForm(empty); load();
  };

  const update = async () => {
    try {
      const { data } = await api.put(`/negotiations/${detail.id}`, {
        site_id: detail.site_id,
        current_rent: parseFloat(detail.current_rent) || 0,
        proposed_rent: detail.proposed_rent != null && detail.proposed_rent !== '' ? parseFloat(detail.proposed_rent) : null,
        max_offer_pct: parseFloat(detail.max_offer_pct) || 15,
        expiration_date: detail.expiration_date, next_action_date: detail.next_action_date,
        next_action_type: detail.next_action_type, priority: detail.priority,
        status: detail.status, notes: detail.notes,
      });
      setDetail(data);  // refresca detail con datos frescos del backend
      load();
      return true;
    } catch (e) {
      alert('Error al guardar: ' + (e?.response?.data?.detail || e.message));
      return false;
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar la negociación de ${detail.site_id}? Se perderá el historial completo.`)) return;
    try {
      await api.delete(`/negotiations/${detail.id}`);
      setDetail(null);
      load();
    } catch (e) {
      alert('Error al eliminar: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const removeInline = async (nid, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('¿Eliminar esta negociación? Se perderá el historial completo.')) return;
    try {
      await api.delete(`/negotiations/${nid}`);
      load();
    } catch (err) {
      alert('Error al eliminar: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const addEvent = async () => {
    if (!event.summary.trim()) return;
    try {
      const r = await api.post(`/negotiations/${detail.id}/events`, event);
      setDetail(r.data);
      setEvent({ type: 'llamada', summary: '' });
      load();
    } catch (e) {
      alert('Error al añadir evento: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const maxOffer = (rent, pct) => rent ? (parseFloat(rent) * (1 + parseFloat(pct)/100)).toFixed(2) : '0';

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const filtered = useMemo(() => items.filter((n) => {
    const site = siteMap[n.site_id];
    return matches(search, site?.name, site?.municipality, site?.address, site?.owner_name, n.notes, n.status, n.priority);
  }), [items, siteMap, search]);

  const eventIcons = { llamada: Phone, visita: Eye, email: Mail, oferta: TrendingUp, nota: FileText };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">ADQUISICIONES · NEGOCIACIÓN</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Negociaciones activas</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Prioridad L1/L2/L3, control de vencimientos y oferta con margen configurable.</p>
        </div>
        <button onClick={() => { setForm(empty); setModal(true); }} className="inline-flex items-center gap-2 bg-[#A3FF12] text-black px-5 py-3 rounded-full font-bold hover:bg-[#b8ff3a] transition-colors text-sm lg:text-base">
          <Plus size={18}/> Nueva negociación
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {['', 'L1', 'L2', 'L3'].map((p) => (
          <button key={p || 'all'} onClick={() => setFilterP(p)}
            className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
              filterP === p ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'border-white/10 text-white/70 hover:border-white/30'
            }`}>{p || 'Todas'}</button>
        ))}
        <span className="w-px bg-white/10 mx-2"/>
        {['', 'abierta', 'pendiente', 'firmada', 'perdida'].map((s) => (
          <button key={s || 'all-s'} onClick={() => setFilterS(s)}
            className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors capitalize ${
              filterS === s ? 'bg-white text-black border-white' : 'border-white/10 text-white/70 hover:border-white/30'
            }`}>{s || 'Cualquier estado'}</button>
        ))}
      </div>
      <div className="mb-6"><SearchBar value={search} onChange={setSearch} placeholder="Buscar por emplazamiento, propietario o notas…" testId="negotiations-search"/></div>

      {items.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-12 text-center">
          <p className="text-white/50">No hay negociaciones todavía.</p>
          <button onClick={() => setModal(true)} className="mt-4 inline-flex items-center gap-2 text-[#A3FF12] font-bold hover:underline">Crear la primera <Plus size={16}/></button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-12 text-center">
          <p className="text-white/50">Ninguna negociación coincide con «{search}».</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((n) => {
            const site = sites.find((s) => s.id === n.site_id);
            return (
              <div key={n.id} onClick={() => setDetail(n)} data-testid={`neg-card-${n.id}`}
                className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 hover:border-[#A3FF12]/40 transition-colors cursor-pointer relative group">
                <button onClick={(e) => removeInline(n.id, e)}
                  data-testid={`btn-remove-inline-${n.id}`}
                  className="absolute top-3 right-3 p-1.5 rounded-lg text-red-400/60 hover:text-red-300 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Eliminar negociación">
                  <Trash2 size={14}/>
                </button>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2"><PriorityBadge p={n.priority}/><StatusBadge s={n.status}/></div>
                  <Calendar className="text-white/30 mr-6" size={16}/>
                </div>
                <h3 className="text-white font-bold text-lg truncate">{site?.name || n.notes || `Negociación ${n.id.slice(0,8)}`}</h3>
                {site && <p className="text-white/50 text-xs truncate flex items-center gap-1 mt-1"><MapPin size={12}/> {site.address || site.municipality}</p>}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="bg-black/40 border border-white/5 rounded-lg p-2">
                    <div className="text-white/40 text-xs">Renta actual</div>
                    <div className="text-white font-bold">{n.current_rent}€</div>
                  </div>
                  <div className="bg-black/40 border border-white/5 rounded-lg p-2">
                    <div className="text-white/40 text-xs">Oferta máx (+{n.max_offer_pct}%)</div>
                    <div className="text-[#A3FF12] font-bold">{maxOffer(n.current_rent, n.max_offer_pct)}€</div>
                  </div>
                </div>
                {n.next_action_date && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-white/60">
                    <Clock size={12}/> Próxima: {n.next_action_type} · {n.next_action_date}
                  </div>
                )}
                {n.expiration_date && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-orange-400">
                    <Calendar size={12}/> Vence: {n.expiration_date}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New negotiation modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Nueva negociación">
        <div className="space-y-4">
          <SitePicker sites={sites} siteId={form.site_id} manualCode={form.site_code_manual}
            onChange={({ siteId, manualCode }) => setForm({ ...form, site_id: siteId, site_code_manual: manualCode })}
            testIdPrefix="neg-new"/>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-white/80 text-sm font-semibold">Renta actual (€)</label>
              <input type="number" step="0.01" value={form.current_rent} onChange={(e) => setForm({...form, current_rent: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Oferta propuesta (€)</label>
              <input type="number" step="0.01" value={form.proposed_rent} onChange={(e) => setForm({...form, proposed_rent: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Max % a ofrecer</label>
              <input type="number" step="0.1" value={form.max_offer_pct} onChange={(e) => setForm({...form, max_offer_pct: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
          </div>
          <div className="text-[#A3FF12] text-sm font-bold">Oferta máxima calculada: {maxOffer(form.current_rent, form.max_offer_pct)}€</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/80 text-sm font-semibold">Vencimiento contrato</label>
              <input type="date" value={form.expiration_date} onChange={(e) => setForm({...form, expiration_date: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Próxima acción</label>
              <input type="date" value={form.next_action_date} onChange={(e) => setForm({...form, next_action_date: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-white/80 text-sm font-semibold">Tipo acción</label>
              <select value={form.next_action_type} onChange={(e) => setForm({...form, next_action_type: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]">
                <option>llamada</option><option>visita</option><option>email</option><option>firma</option>
              </select>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Prioridad</label>
              <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]">
                <option>L1</option><option>L2</option><option>L3</option>
              </select>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Estado</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]">
                <option>abierta</option><option>pendiente</option><option>firmada</option><option>perdida</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">Notas</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3}
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(false)} className="px-5 py-2.5 text-white/70 hover:text-white">Cancelar</button>
            <button onClick={create} className="bg-[#A3FF12] text-black px-6 py-2.5 rounded-xl font-bold hover:bg-[#b8ff3a] transition-colors">Crear</button>
          </div>
        </div>
      </Modal>

      {/* Detail / edit modal */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title="Detalle negociación">
        {detail && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/80 text-xs font-bold">Renta actual</label>
                <input type="number" value={detail.current_rent} onChange={(e) => setDetail({...detail, current_rent: parseFloat(e.target.value)||0})}
                  className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-white/80 text-xs font-bold">Oferta propuesta</label>
                <input type="number" value={detail.proposed_rent || ''} onChange={(e) => setDetail({...detail, proposed_rent: parseFloat(e.target.value)||0})}
                  className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-white/80 text-xs font-bold">Prioridad</label>
                <select value={detail.priority} onChange={(e) => setDetail({...detail, priority: e.target.value})}
                  className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-3 py-2 text-sm">
                  <option>L1</option><option>L2</option><option>L3</option>
                </select>
              </div>
              <div>
                <label className="text-white/80 text-xs font-bold">Estado</label>
                <select value={detail.status} onChange={(e) => setDetail({...detail, status: e.target.value})}
                  className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-3 py-2 text-sm">
                  <option>abierta</option><option>pendiente</option><option>firmada</option><option>perdida</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={async () => { const ok = await update(); if (ok) setDetail(null); }}
                data-testid="btn-save-negotiation"
                className="flex-1 bg-[#A3FF12] text-black py-2.5 rounded-xl font-bold hover:bg-[#b8ff3a] transition-colors flex items-center justify-center gap-2">
                <Save size={16}/> Guardar cambios
              </button>
              <button onClick={remove}
                data-testid="btn-delete-negotiation"
                className="px-4 py-2.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-xl font-bold flex items-center gap-2">
                <Trash2 size={16}/> Eliminar
              </button>
            </div>

            {/* Fiscalidad propietario */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="text-[#A3FF12]" size={16}/>
                <h4 className="text-white font-bold">Fiscalidad del propietario</h4>
                {taxLoading && <Loader2 className="animate-spin text-[#A3FF12]" size={14}/>}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="text-white/60 text-[10px] font-bold uppercase">Tipo propietario</label>
                  <select value={taxParams.owner_type} onChange={(e) => setTaxParams({...taxParams, owner_type: e.target.value})}
                    className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-2 py-1.5 text-xs">
                    <option value="particular">Particular (IRPF)</option>
                    <option value="empresa">Empresa (IS)</option>
                    <option value="ccpp">CCPP (vecinos)</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/60 text-[10px] font-bold uppercase">Zona del inmueble</label>
                  <select value={taxParams.region} onChange={(e) => setTaxParams({...taxParams, region: e.target.value})}
                    className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-2 py-1.5 text-xs">
                    <option value="peninsula">Península / Baleares</option>
                    <option value="canarias">Canarias</option>
                    <option value="ceuta">Ceuta</option>
                    <option value="melilla">Melilla</option>
                  </select>
                </div>
                {taxParams.owner_type === 'ccpp' && (
                  <div>
                    <label className="text-white/60 text-[10px] font-bold uppercase">Nº vecinos</label>
                    <input type="number" min="1" value={taxParams.num_owners} onChange={(e) => setTaxParams({...taxParams, num_owners: e.target.value})}
                      className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-2 py-1.5 text-xs"/>
                  </div>
                )}
                {taxParams.owner_type === 'particular' && (
                  <div>
                    <label className="text-white/60 text-[10px] font-bold uppercase">Otros ingresos anuales (€)</label>
                    <input type="number" min="0" value={taxParams.other_income} onChange={(e) => setTaxParams({...taxParams, other_income: e.target.value})}
                      placeholder="0" className="w-full mt-1 bg-black border border-white/15 text-white rounded-lg px-2 py-1.5 text-xs"/>
                  </div>
                )}
                {taxParams.owner_type === 'empresa' && (
                  <label className="flex items-center gap-2 text-white/70 text-xs col-span-2 cursor-pointer mt-1">
                    <input type="checkbox" checked={taxParams.is_new_company} onChange={(e) => setTaxParams({...taxParams, is_new_company: e.target.checked})}
                      className="accent-[#A3FF12] w-4 h-4"/>
                    Nueva sociedad (IS reducido 15%)
                  </label>
                )}
              </div>

              {taxData && (
                <div className="bg-[#0a0a0a] border border-[#A3FF12]/30 rounded-xl p-4 space-y-2">
                  <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">{taxData.regime}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-black/40 border border-white/5 rounded-lg p-2">
                      <div className="text-white/50">Renta anual</div>
                      <div className="text-white font-black text-base">{taxData.annual_rent.toLocaleString('es-ES')}€</div>
                    </div>
                    <div className="bg-black/40 border border-red-500/30 rounded-lg p-2">
                      <div className="text-red-300">Impuesto final</div>
                      <div className="text-red-400 font-black text-base">{taxData.final_tax.toLocaleString('es-ES')}€</div>
                    </div>
                    <div className="bg-black/40 border border-[#A3FF12]/30 rounded-lg p-2">
                      <div className="text-[#A3FF12]">Neto propietario</div>
                      <div className="text-[#A3FF12] font-black text-base">{taxData.net_annual.toLocaleString('es-ES')}€</div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs pt-2 border-t border-white/5">
                    <span className="text-white/60">Tipo efectivo total</span>
                    <span className="text-white font-bold">{taxData.effective_total_pct}%</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/60">Neto/mes</span>
                    <span className="text-[#A3FF12] font-bold">{taxData.monthly_net.toLocaleString('es-ES')}€</span>
                  </div>
                  {taxParams.owner_type === 'ccpp' && (
                    <div className="flex justify-between text-xs">
                      <span className="text-white/60">Por vecino/mes</span>
                      <span className="text-white font-bold">{(taxData.monthly_net / (parseInt(taxParams.num_owners) || 1)).toFixed(2)}€</span>
                    </div>
                  )}
                  {taxData.bonification_pct > 0 && (
                    <div className="bg-[#A3FF12]/10 border border-[#A3FF12]/20 text-[#A3FF12] text-xs rounded-lg px-3 py-2 leading-relaxed mt-2">
                      <strong>✓ {taxData.bonification_label}</strong> (−{taxData.bonification_pct}% sobre cuota)
                    </div>
                  )}
                  {taxData.bonification_label && taxData.bonification_pct === 0 && taxParams.region === 'canarias' && (
                    <div className="bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs rounded-lg px-3 py-2 leading-relaxed mt-2">
                      <strong>ℹ Canarias:</strong> {taxData.bonification_label}
                    </div>
                  )}
                  {taxData.brackets && taxData.brackets.length > 0 && (
                    <div className="pt-2 border-t border-white/5">
                      <div className="text-white/40 text-[10px] uppercase mb-1">Desglose por tramo {taxParams.owner_type === 'ccpp' ? '(por vecino)' : ''}</div>
                      {taxData.brackets.map((b, i) => (
                        <div key={i} className="flex justify-between text-[11px]">
                          <span className="text-white/60">{b.pct}% sobre {(b.amount_in_bracket || b.amount).toLocaleString('es-ES')}€</span>
                          <span className="text-white/80">{(b.tax_in_bracket || b.tax).toLocaleString('es-ES')}€</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-white/30 text-[10px] italic pt-1">* IRPF escala estatal+autonómica media 2025. Sin reducción 60% (uso no residencial).</p>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 mb-3"><History className="text-[#A3FF12]" size={16}/><h4 className="text-white font-bold">Histórico</h4></div>
              <div className="flex gap-2 mb-3">
                <select value={event.type} onChange={(e) => setEvent({...event, type: e.target.value})}
                  className="bg-black border border-white/15 text-white rounded-lg px-3 py-2 text-sm">
                  <option value="llamada">Llamada</option>
                  <option value="visita">Visita</option>
                  <option value="email">Email</option>
                  <option value="oferta">Oferta</option>
                  <option value="nota">Nota</option>
                </select>
                <input value={event.summary} onChange={(e) => setEvent({...event, summary: e.target.value})} placeholder="Resumen..."
                  className="flex-1 bg-black border border-white/15 text-white rounded-lg px-3 py-2 text-sm"/>
                <button onClick={addEvent} className="bg-white/5 border border-white/10 text-white px-3 rounded-lg text-sm hover:border-[#A3FF12]/40"><Plus size={16}/></button>
              </div>
              <div className="space-y-2 max-h-60 overflow-auto">
                {(detail.history || []).slice().reverse().map((h) => {
                  const Ic = eventIcons[h.type] || FileText;
                  return (
                    <div key={h.id} className="flex items-start gap-3 bg-black/40 border border-white/5 rounded-lg p-3">
                      <Ic size={14} className="text-[#A3FF12] mt-0.5"/>
                      <div className="flex-1 text-sm">
                        <div className="text-white">{h.summary}</div>
                        <div className="text-white/40 text-xs mt-0.5 uppercase">{h.type} · {new Date(h.date).toLocaleDateString('es-ES')}</div>
                      </div>
                    </div>
                  );
                })}
                {(!detail.history || detail.history.length === 0) && <p className="text-white/40 text-sm text-center py-4">Sin eventos aún</p>}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Negociaciones;
